/**
 * DealNumberInput — text input that, on blur (or after a 400 ms typing
 * pause), looks up the deal # in DealerBuilt and fires `onLookupResult`
 * with the auto-fill payload (which includes the sold stock #).
 *
 * Keeps the row's own onChange handler responsible for the field value;
 * this component only owns lookup orchestration. Non-numeric input is
 * PRESERVED and flagged invalid (red border, lookup suppressed) rather than
 * sanitized — stripping "12O34" to "1234" would look up a different valid
 * deal. The backend's save-entry 400 stays the authoritative guard.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { lookupDealNumber } from '../../services/nutSheetApi';
import { DealLookupResponse, isNumericDealNumber } from '../../types/nutSheet';
import { cellBase, textCell } from './cellStyles';

interface DealNumberInputProps {
  locationId: number;
  date: string;
  value: string;
  onChange: (v: string) => void;
  onLookupResult: (result: DealLookupResponse) => void;
  disabled?: boolean;
}

/** Everything that identifies one lookup: the deal # AND the context it was
 *  issued under. A response is applied only while all three still match. */
interface LookupKey {
  dealNumber: string;
  locationId: number;
  date: string;
}

const keysEqual = (a: LookupKey | null, b: LookupKey): boolean =>
  a !== null &&
  a.dealNumber === b.dealNumber &&
  a.locationId === b.locationId &&
  a.date === b.date;

export const DealNumberInput: React.FC<DealNumberInputProps> = ({
  locationId,
  date,
  value,
  onChange,
  onLookupResult,
  disabled,
}) => {
  const [busy, setBusy] = useState(false);
  // Last lookup that COMPLETED successfully — advanced only on success so a
  // transient API failure can be retried by blur/re-entry. Keyed by context so
  // a location/date change permits a fresh lookup of the same #. Seeded from
  // the mounted row so saved rows don't re-fire the lookup on page load;
  // cleared when the field is emptied so re-entering the same deal # performs
  // a fresh lookup (the documented way to refresh open ROs etc.).
  const lastCompleted = useRef<LookupKey | null>({
    dealNumber: value.trim(),
    locationId,
    date,
  });
  // Request currently in flight — dedupes the debounce-then-blur double fire
  // without permanently suppressing retries.
  const inFlight = useRef<LookupKey | null>(null);
  // The CURRENT input + context, updated every render. A resolved lookup is
  // applied only if it still matches — typing on, clearing the field, an
  // invalid edit, or a location/date change all orphan an in-flight request,
  // so a slow older response can never overwrite newer input.
  const latest = useRef<LookupKey>({ dealNumber: value.trim(), locationId, date });
  latest.current = { dealNumber: value.trim(), locationId, date };

  const trimmed = value.trim();
  const invalid = trimmed.length > 0 && !isNumericDealNumber(trimmed);

  const triggerLookup = async (raw: string) => {
    const key: LookupKey = { dealNumber: raw.trim(), locationId, date };
    if (
      !key.dealNumber ||
      !isNumericDealNumber(key.dealNumber) ||
      keysEqual(lastCompleted.current, key) ||
      keysEqual(inFlight.current, key)
    ) {
      return;
    }
    inFlight.current = key;
    setBusy(true);
    const res = await lookupDealNumber(locationId, key.dealNumber, date);
    if (keysEqual(inFlight.current, key)) {
      inFlight.current = null;
      setBusy(false);
    }
    // Stale response: the input or context moved on while this request was in
    // flight — drop it (a newer trigger, if any, owns the field now).
    if (!keysEqual(latest.current, key)) return;
    if (res.success && res.data) {
      lastCompleted.current = key;
      onLookupResult(res.data);
    }
  };

  // Debounce typing-driven lookups; a cleared field resets the completed
  // marker so the same deal # can be looked up again.
  useEffect(() => {
    if (!value) {
      lastCompleted.current = null;
      return;
    }
    const handle = setTimeout(() => triggerLookup(value), 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, locationId, date]);

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => triggerLookup(e.target.value)}
        disabled={disabled}
        placeholder="Deal #"
        inputMode="numeric"
        aria-invalid={invalid || undefined}
        title={invalid ? 'Deal # must be digits only' : undefined}
        className={`${
          invalid ? `${cellBase} border-red-500 ring-1 ring-red-300` : textCell
        } w-24 pr-6 disabled:opacity-60`}
      />
      {busy && (
        <ArrowPathIcon className="absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 animate-spin" />
      )}
    </div>
  );
};
